import { useState } from 'react';
import { Loader2, Play, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function LegBadge({ empty, label }) {
  if (empty) {
    return (
      <Badge variant="destructive" className="text-xs">
        <XCircle className="h-3 w-3 mr-1" />
        {label}: EMPTY
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-green-400 text-green-600 text-xs">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      {label}: populated
    </Badge>
  );
}

export function BROSSupplierDataDiagnosticPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [period, setPeriod] = useState('ytd');

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosSupplierDataDiagnostic(period);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const b = result?.bookings;
  const si = result?.supplier_invoices;
  const legs = result?.data_legs;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Supplier Data Diagnostic</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Reports actual COGS, supplier bill, and supplier payment values.
          Determines whether the high-margin caveat fires because data is absent.
        </p>

        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
            {['ytd', 'last_12m', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  period === p
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p === 'ytd' ? 'YTD' : p === 'last_12m' ? 'Last 12M' : 'All'}
              </button>
            ))}
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </div>

        {result && (
          <div className="space-y-4">
            {/* Leg status badges */}
            <div className="flex flex-wrap gap-2">
              <LegBadge empty={legs?.cogs_empty} label="COGS" />
              <LegBadge empty={legs?.bills_empty} label="Supplier Bills" />
              <LegBadge empty={legs?.payments_empty} label="Payments" />
            </div>

            {/* Verdict */}
            <div className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 border ${
              legs?.cogs_empty || legs?.bills_empty
                ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                : 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800'
            }`}>
              {(legs?.cogs_empty || legs?.bills_empty)
                ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
              }
              <span>{result.verdict}</span>
            </div>

            {/* Data grids */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Booking COGS */}
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Booking COGS (supplier_total)</p>
                <p className="text-xs text-muted-foreground">
                  {result.period_label} ({result.date_range?.start} to {result.date_range?.end})
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <span className="text-muted-foreground">Bookings</span>
                  <span className="tabular-nums">{fmtInt(b?.count)}</span>
                  <span className="text-muted-foreground">Total turnover</span>
                  <span className="tabular-nums">{fmt(b?.total_turnover)}</span>
                  <span className="text-muted-foreground font-medium">Total COGS</span>
                  <span className="tabular-nums font-medium">{fmt(b?.total_cogs)}</span>
                  <span className="text-muted-foreground">Gross margin</span>
                  <span className="tabular-nums">{fmt(b?.total_gross_margin)}</span>
                  <span className="text-muted-foreground">Margin %</span>
                  <span className="tabular-nums">{b?.margin_pct != null ? `${b.margin_pct.toFixed(1)}%` : '-'}</span>
                  <span className="text-muted-foreground">With supplier cost</span>
                  <span className="tabular-nums">{fmtInt(b?.nonzero_supplier_total_count)}</span>
                  <span className="text-muted-foreground">Zero/missing</span>
                  <span className="tabular-nums text-amber-600">{fmtInt(b?.zero_supplier_total_count)}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Source: {b?.source}</p>
              </div>

              {/* Supplier invoices */}
              <div className="border rounded-lg p-3 space-y-1">
                <p className="text-sm font-medium">Supplier Invoices (bills + payments)</p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <span className="text-muted-foreground">Invoice count</span>
                  <span className="tabular-nums">{fmtInt(si?.count)}</span>
                  <span className="text-muted-foreground font-medium">Total billed</span>
                  <span className="tabular-nums font-medium">{fmt(si?.total_bills)}</span>
                  <span className="text-muted-foreground font-medium">Total paid</span>
                  <span className="tabular-nums font-medium">{fmt(si?.total_paid)}</span>
                  <span className="text-muted-foreground">Outstanding</span>
                  <span className="tabular-nums">{fmt(si?.total_outstanding)}</span>
                  <span className="text-muted-foreground">With amount</span>
                  <span className="tabular-nums">{fmtInt(si?.nonzero_amount_count)}</span>
                  <span className="text-muted-foreground">Zero/missing</span>
                  <span className="tabular-nums text-amber-600">{fmtInt(si?.zero_amount_count)}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Source: {si?.source}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
